/**
 * Typed (JSDoc) and validated boundary for the NeuroViz read APIs.
 *
 * The server owns the scientific contract.  This module deliberately keeps
 * response validation close to fetch so malformed or unexpectedly shaped data
 * cannot enter scene/query/evidence state.
 */

export const csrfToken = () => globalThis.document?.querySelector?.('[name=csrfmiddlewaretoken]')?.value || decodeURIComponent((globalThis.document?.cookie || '').split('; ').find(item => item.startsWith('csrftoken='))?.slice(10) || '');

export class NeuroVizApiError extends Error {
  constructor(message, { code = 'request_failed', status = 0, detail = null } = {}) {
    super(message);
    this.name = 'NeuroVizApiError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isString = (value) => typeof value === 'string' && value.length > 0;
const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const assertRecord = (value, label) => {
  if (!isRecord(value)) throw new NeuroVizApiError(`${label} response is invalid`, { code: 'invalid_response' });
  return value;
};
const assertField = (value, field, label = 'response') => {
  if (!isString(value[field])) throw new NeuroVizApiError(`${label}.${field} is required`, { code: 'invalid_response' });
};
const assertList = (value, field, label = 'response') => {
  if (!Array.isArray(value[field])) throw new NeuroVizApiError(`${label}.${field} must be an array`, { code: 'invalid_response' });
};
const assertStableIds = (items, field, label) => {
  items.forEach((item, index) => {
    if (!isRecord(item) || !isString(item[field])) {
      throw new NeuroVizApiError(`${label}[${index}].${field} is required`, { code: 'invalid_response' });
    }
  });
};

const validators = {
  catalog(payload) {
    assertRecord(payload, 'catalog');
    assertList(payload, 'datasets', 'catalog');
    assertStableIds(payload.datasets, 'dataset_id', 'catalog.datasets');
    if (!isNumber(payload.count)) throw new NeuroVizApiError('catalog.count is required', { code: 'invalid_response' });
    return payload;
  },
  manifest(payload) {
    assertRecord(payload, 'manifest');
    assertField(payload, 'dataset_id', 'manifest');
    assertField(payload, 'version', 'manifest');
    assertList(payload, 'coordinate_spaces', 'manifest');
    if (!isRecord(payload.capabilities)) throw new NeuroVizApiError('manifest.capabilities must be an object', { code: 'invalid_response' });
    return payload;
  },
  atlas(payload) { assertRecord(payload, 'atlas'); assertList(payload, 'regions', 'atlas'); assertStableIds(payload.regions, 'region_id', 'atlas.regions'); return payload; },
  meshes(payload) { assertRecord(payload, 'meshes'); assertList(payload, 'meshes', 'meshes'); assertStableIds(payload.meshes, 'mesh_id', 'meshes.meshes'); return payload; },
  spatial(payload) {
    assertRecord(payload, 'spatial');
    assertList(payload, 'entities', 'spatial');
    assertStableIds(payload.entities, 'entity_id', 'spatial.entities');
    if (!isNumber(payload.count)) throw new NeuroVizApiError('spatial.count is required', { code: 'invalid_response' });
    return payload;
  },
  evidence(payload) { assertRecord(payload, 'evidence'); return payload; },
  export(payload) { assertRecord(payload, 'export'); assertList(payload, 'rows', 'export'); return payload; },
};

const encode = (value) => encodeURIComponent(String(value));
const queryString = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.set(key, isRecord(value) ? JSON.stringify(value) : Array.isArray(value) ? value.join(',') : String(value));
  });
  const result = query.toString();
  return result ? `?${result}` : '';
};

export class NeuroVizApiClient {
  /** @param {{baseUrl?: string, fetchImpl?: typeof fetch}} options */
  constructor({ baseUrl = '/explorer/api', fetchImpl } = {}) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    // Window.fetch is a method and must retain its receiver in browsers.
    // Keep injected implementations untouched for tests and server callers.
    this.fetchImpl = fetchImpl || (typeof window !== 'undefined' ? window.fetch.bind(window) : fetch);
  }

  /** @param {string} path @param {object} params @param {AbortSignal|undefined} signal */
  async request(path, params = {}, signal, options = {}) {
    let response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}${queryString(params)}`, { signal, headers: { Accept: 'application/json', ...(options.headers || {}) }, ...options, signal });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      throw new NeuroVizApiError(error?.message || 'Network request failed', { code: 'network_error' });
    }
    let payload;
    try {
      if (path === '/merfish/collection/tiles/' && response.body?.getReader) {
        const reader = response.body.getReader(), decoder = new TextDecoder('utf-8', {fatal:true});
        let bytes = 0, text = '';
        try {
          while (true) {
            const chunk = await reader.read();
            if (chunk.done) break;
            bytes += chunk.value.byteLength;
            if (bytes > 512 * 1024 * 1024) { await reader.cancel(); throw new Error('Collection delivery exceeds 512 MiB'); }
            text += decoder.decode(chunk.value, {stream:true});
          }
          text += decoder.decode();
          payload = JSON.parse(text);
          if (payload && typeof payload === 'object') Object.defineProperty(payload, Symbol.for('neuroviz.decodedBytes'), {value:bytes});
        } finally { reader.releaseLock(); }
      } else payload = await response.json();
    } catch (error) { if (error?.name === 'AbortError') throw error; throw new NeuroVizApiError('Server returned invalid JSON', { code: 'invalid_response', status: response.status }); }
    const exportStatus = path.startsWith('/merfish/collection/exports/') && typeof payload?.export_id === 'string' && ['failed','cancelled','expired'].includes(payload.state);
    if (!response.ok || (payload?.error && !exportStatus)) {
      const detail = typeof payload?.error === 'string' ? {code: payload.error} : payload?.error;
      throw new NeuroVizApiError(detail?.message || `Request failed (${response.status})`, { code: detail?.code || 'request_failed', status: response.status, detail });
    }
    return payload;
  }

  async collectionRequest(path, params = {}, signal, body) {
    const options = {cache: 'no-store', credentials: 'same-origin'};
    if (body !== undefined) Object.assign(options, {
      method: 'POST', body: JSON.stringify(body),
      headers: {'Content-Type': 'application/json', 'X-CSRFToken': csrfToken()},
    });
    return assertRecord(await this.request(`/merfish/collection/${path}`, params, signal, options), 'collection');
  }
  getCollectionEvidence(entityId, params, signal) { return this.request(`/entities/${encode(entityId)}/`, params, signal, {cache: 'no-store', credentials: 'same-origin'}); }
  createCollectionQuery(body, signal) { return this.collectionRequest('queries/', {}, signal, body); }
  getCollectionQuery(id, signal) { return this.collectionRequest(`queries/${encode(id)}/`, {}, signal); }
  cancelCollectionJob(id, signal) { return this.collectionRequest(`jobs/${encode(id)}/cancel/`, {}, signal, {}); }
  createCollectionOrder(id, body, signal) { return this.collectionRequest(`queries/${encode(id)}/orders/`, {}, signal, body); }
  getCollectionTable(id, params, signal) { return this.collectionRequest(`queries/${encode(id)}/table/`, params, signal); }
  getCollectionAdjacent(id, params, signal) { return this.collectionRequest(`queries/${encode(id)}/adjacent/`, params, signal); }
  createCollectionTiles(body, signal) { return this.collectionRequest('tiles/', {}, signal, body); }
  getCollectionTiles(id, signal, colorGeneId) { return this.collectionRequest('tiles/', {spatial_id: id, ...(colorGeneId ? {color_gene_id:colorGeneId} : {})}, signal); }
  createCollectionSummary(body, signal) { return this.collectionRequest('summaries/', {}, signal, body); }
  getCollectionSummary(id, signal) { return this.collectionRequest(`summaries/${encode(id)}/`, {}, signal); }
  estimateCollectionExport(body, signal) { return this.collectionRequest('exports/estimate/', {}, signal, body); }
  createCollectionExport(body, signal) { return this.collectionRequest('exports/', {}, signal, body); }
  getCollectionExport(id, signal) { return this.collectionRequest(`exports/${encode(id)}/`, {}, signal); }

  async getDatasetCatalog(params = {}, signal) { return validators.catalog(await this.request('/datasets/', params, signal)); }
  async getManifest(datasetId, version, signal) {
    const suffix = version
      ? `/datasets/${encode(datasetId)}/${encode(version)}/`
      : `/datasets/${encode(datasetId)}/`;
    return validators.manifest(await this.request(suffix, {}, signal));
  }
  async searchAtlasRegions(params = {}, signal) { return validators.atlas(await this.request('/atlas/regions/', params, signal)); }
  async getAtlasMeshes(params = {}, signal) { return validators.meshes(await this.request('/atlas/meshes/', params, signal)); }
  async querySpatialEntities(params = {}, signal) { return validators.spatial(await this.request('/entities/', params, signal)); }
  async queryCells(params = {}, signal) { return validators.spatial(await this.request('/cells/', { ...params, entity_type: 'cell' }, signal)); }
  async querySpatialWindow(params = {}, signal) { return validators.spatial(await this.request('/spatial/windows/', params, signal)); }
  async getEvidence(entityId, signal, identity = {}) { return validators.evidence(await this.request(`/entities/${encode(entityId)}/`, identity, signal)); }
  async getMerfishPoints(params, signal) {
    const data = assertRecord(await this.request('/merfish/points/', params, signal), 'points');
    assertList(data, 'points');
    assertStableIds(data.points, 'entity_id', 'points');
    if (data.points.length > 10000 || data.points.some((point) => !Array.isArray(point.position) || point.position.length !== 3 || !point.position.every(isNumber))) throw new NeuroVizApiError('Invalid spatial positions', { code: 'invalid_response' });
    return { ...data, entities: data.points, count: data.counts.matching };
  }
  async getMerfishAdjacent(params, signal) {
    const data = assertRecord(await this.request('/merfish/adjacent/', params, signal), 'adjacent');
    if (!['matching', 'no_match'].includes(data.selection_state)) throw new NeuroVizApiError('Invalid adjacency', { code: 'invalid_response' });
    return data;
  }
  async getActivity(params = {}, signal) { return this.request('/activity/', params, signal); }
  async getProvenance(params = {}, signal) { return this.request('/provenance/', params, signal); }
  async createShareState(state, signal) {
    return this.request('/share/', {}, signal, { method: 'POST', body: JSON.stringify({ state }), headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() } });
  }
  async getShareState(token, signal) { return this.request(`/share/${encode(token)}/`, {}, signal, { cache: 'no-store' }); }
  async restoreCollectionShare(token, signal) {
    return this.request(`/share/${encode(token)}/`, {}, signal, {
      method: 'POST', cache: 'no-store', body: '{}',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
    });
  }
  async exportEntities(params = {}, signal) {
    const data = validators.export(await this.request('/entities/export/', params, signal));
    const metadata = data.metadata || data;
    if (metadata.dataset_id !== params.dataset_id || metadata.version !== params.version ||
        metadata.scope !== (params.scope || 'filtered') || data.rows.length > 10000 ||
        metadata.count !== data.rows.length ||
        (params.scope === 'selection' && (data.rows.length !== 1 || data.rows[0].entity_id !== params.selected_entity))) {
      throw new NeuroVizApiError('Export response does not match the requested scope', { code: 'invalid_response' });
    }
    return data;
  }
  async downloadEntities(params = {}, signal) {
    const response = await this.fetchImpl(`${this.baseUrl}/entities/export/${queryString({ ...params, format: 'csv' })}`, {
      signal,
      headers: { Accept: 'text/csv' },
    });
    if (!response.ok) {
      let payload = {};
      try { payload = await response.json(); } catch { /* preserve the HTTP status */ }
      const detail = payload?.error;
      throw new NeuroVizApiError(detail?.message || `Request failed (${response.status})`, {
        code: detail?.code || 'request_failed', status: response.status, detail,
      });
    }
    if (!response.headers.get('Content-Type')?.toLowerCase().startsWith('text/csv')) {
      throw new NeuroVizApiError('Server did not return a CSV export', { code: 'invalid_response', status: response.status });
    }
    return { blob: await response.blob(), filename: response.headers.get('Content-Disposition') || null };
  }
}

export { validators as responseValidators };
